import React, { useState, useEffect, useContext } from 'react';
import { Typography, Paper, Button, Box, CircularProgress, Table, TableHead, TableRow, TableCell, TableBody, Checkbox } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import TanStackTable from '../components/TanStackTable';
import { getSuppliers, deleteMultipleSuppliers } from '../services/supplierService';
import { OrgContext } from '../contexts/OrgContext'; // Import OrgContext
import Papa from 'papaparse';
import BulkActionResults from '../components/BulkActionResults';

const SuppliersList = () => {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { orgId } = useContext(OrgContext); // Access orgId from OrgContext
  const [deleteMode, setDeleteMode] = useState(false);
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [deleteResults, setDeleteResults] = useState(null);
  
  // Pagination and filtering state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
    hasNextPage: false,
    hasPrevPage: false
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('supplier_name');
  const [sortOrder, setSortOrder] = useState('ASC');

  const fetchSuppliers = React.useCallback(async (customOptions = {}) => {
    if (!orgId) return;
    
    setLoading(true);
    try {
      const requestOptions = {
        page: customOptions.page !== undefined ? customOptions.page : pagination.page,
        limit: customOptions.limit !== undefined ? customOptions.limit : pagination.limit,
        search: customOptions.search !== undefined ? customOptions.search : searchQuery,
        sortBy: customOptions.sortBy !== undefined ? customOptions.sortBy : sortBy,
        sortOrder: customOptions.sortOrder !== undefined ? customOptions.sortOrder : sortOrder
      };

      const response = await getSuppliers(orgId, requestOptions);
      
      // Check if response is paginated
      if (response.data && Array.isArray(response.data)) {
        setRowData(response.data);
        setPagination(response.pagination);
      } else if (Array.isArray(response)) {
        // Fallback for non-paginated response
        setRowData(response);
      }
    } finally {
      setLoading(false);
    }
  }, [orgId, pagination.page, pagination.limit, searchQuery, sortBy, sortOrder]);

  useEffect(() => {
    if (orgId) {
      fetchSuppliers();
    }
  }, [fetchSuppliers, orgId]);

  const columns = [
    {
      accessorKey: 'id',
      header: 'ID',
    },
    {
      accessorKey: 'supplier_name',
      header: 'Name',
    },
    {
      accessorKey: 'email',
      header: 'Email',
    },
    {
      accessorKey: 'contact_number',
      header: 'Phone',
    },
  ];

  const handleRowClick = (row) => {
    navigate(`/${orgId}/suppliers/${row.id}`);
  };

  const handleDownload = async () => {
    try {
      setLoading(true);
      // Fetch all suppliers without pagination limits
      const response = await getSuppliers(orgId, {
        page: 1,
        limit: 999999, // Large number to get all suppliers
        search: '',
        sortBy: 'supplier_name',
        sortOrder: 'ASC'
      });
      
      // Extract suppliers from response data
      const allSuppliers = response.data || response || [];
      
      const exportData = allSuppliers.map(supplier => ({
        'Supplier ID': supplier.id,
        'Name': supplier.supplier_name,
        'Email': supplier.email,
        'Phone': supplier.contact_number,
        'Address': supplier.address,
        'City': supplier.city,
        'State': supplier.state,
        'Country': supplier.country,
        'Postal Code': supplier.postal_code,
        'Tax ID': supplier.tax_id
      }));

      const csv = Papa.unparse(exportData);
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const now = new Date();
      const isoDate = now.toISOString().slice(0, 10); // yyyy-mm-dd format
      const uniqueId = `${String(now.getHours()).padStart(2, '0')}${String(now.getMinutes()).padStart(2, '0')}${String(now.getSeconds()).padStart(2, '0')}${String(now.getMilliseconds()).padStart(3, '0')}`;
      link.href = URL.createObjectURL(blob);
      link.setAttribute('download', `${isoDate}_suppliers_${uniqueId}.csv`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading suppliers list:', error);
      // You might want to show an error message to the user here
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    setDeleteMode(true);
  };

  const handleConfirmDelete = async () => {
    try {
      const response = await deleteMultipleSuppliers(selectedSuppliers);
      setDeleteResults(response);
      await fetchSuppliers();
    } catch (error) {
      console.error('Error deleting suppliers:', error);
    } finally {
      setDeleteMode(false);
      setSelectedSuppliers([]);
    }
  };

  return (
    <Paper sx={{
      p: 2,
      bgcolor: 'background.paper',
      color: 'text.primary',
      backdropFilter: 'saturate(140%) blur(6px)',
      border: '1px solid',
      borderColor: 'divider',
    }}>
      <Typography variant="h5" gutterBottom>
        Suppliers
      </Typography>
      {deleteResults && (
        <BulkActionResults 
          results={deleteResults} 
          actionType="deletion"
          onClose={() => setDeleteResults(null)}
        />
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {!deleteMode ? (
          <>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/suppliers/add`)}>
              Add Supplier
            </Button>
            <Button variant="contained" onClick={() => navigate(`/${orgId}/suppliers/bulk-import`)}>
              Add Suppliers in Bulk
            </Button>
            <Button variant="contained" onClick={handleDownload}>
              Download Suppliers List
            </Button>
            <Button 
              variant="contained" 
              onClick={() => navigate(`/${orgId}/accounts-payable`)}
            >
              View Accounts Payable
            </Button>
            <Button variant="contained" color="error" onClick={handleDeleteClick}>
              Delete Suppliers
            </Button>
          </>
        ) : (
          <>
            <Button variant="contained" color="error" onClick={handleConfirmDelete}>
              Confirm Delete
            </Button>
            <Button variant="contained" onClick={() => setDeleteMode(false)}>
              Cancel
            </Button>
          </>
        )}
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          {!deleteMode && (
            <TanStackTable
              data={rowData}
              columns={columns}
              onRowClick={handleRowClick}
              enablePagination={true}
              pagination={pagination}
              onPaginationChange={(newPage, newLimit) => {
                console.log('Suppliers pagination change requested:', newPage, newLimit);
                setPagination(prev => ({...prev, page: newPage, limit: newLimit}));
                fetchSuppliers({page: newPage, limit: newLimit});
              }}
              searchQuery={searchQuery}
              onSearchChange={(search) => {
                setSearchQuery(search);
                setPagination(prev => ({...prev, page: 1}));
                fetchSuppliers({search, page: 1});
              }}
              sortBy={sortBy}
              sortOrder={sortOrder}
              onSortChange={(sortBy, sortOrder) => {
                setSortBy(sortBy);
                setSortOrder(sortOrder);
                fetchSuppliers({sortBy, sortOrder});
              }}
            />
          )}
          {deleteMode && (
            <Box>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Select</TableCell>
                    <TableCell>ID</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rowData.map((supplier) => (
                    <TableRow key={supplier.id}>
                      <TableCell>
                        <Checkbox
                          checked={selectedSuppliers.includes(supplier.id)}
                          onChange={() => {
                            setSelectedSuppliers((prev) =>
                              prev.includes(supplier.id)
                                ? prev.filter((id) => id !== supplier.id)
                                : [...prev, supplier.id]
                            );
                          }}
                        />
                      </TableCell>
                      <TableCell>{supplier.id}</TableCell>
                      <TableCell>{supplier.supplier_name}</TableCell>
                      <TableCell>{supplier.email}</TableCell>
                      <TableCell>{supplier.contact_number}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </>
      )}
    </Paper>
  );
};

export default SuppliersList;
